import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Radio, MapPin, User, Phone, Calendar, Clock, CheckCircle,
  Flag, Image as ImageIcon, Lock, X, Loader, Download, CheckSquare, Square,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * AlyOrderDetailPage — Read-only National order review screen for Aly.
 *
 * What Aly can do here:
 *   • Review every relevant order field (no editing)
 *   • View inspector-uploaded photos
 *   • Click Transmission → confirm modal → counts toward his payment totals
 *
 * What Aly CANNOT do (by design — buttons never render):
 *   • Mark Urgent / Save Changes
 *   • End of Day Close / Send to Hold / Admin Review
 *   • Any status change, any field edit, any delete
 */
const AlyOrderDetailPage = ({ Layout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showTransmitModal, setShowTransmitModal] = useState(false);
  const [transmitting, setTransmitting] = useState(false);
  const [transmitErr, setTransmitErr] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/aly/order/${orderId}`);
      setOrder(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load order");
    } finally { setLoading(false); }
  }, [orderId]);
  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const confirmTransmit = async () => {
    setTransmitting(true); setTransmitErr(null);
    try {
      await axios.post(`${API}/aly/order/${orderId}/transmit`, {});
      setShowTransmitModal(false);
      await fetchOrder();
    } catch (e) {
      setTransmitErr(e.response?.data?.detail || "Failed to transmit");
    } finally { setTransmitting(false); }
  };

  const nowLabel = new Date().toLocaleString();
  const apptDate = order?.appointment_real_date || order?.appointment_sg_date || "—";
  const apptTime = order?.appointment_real_time || order?.appointment_sg_time || order?.appointment_window || "—";
  const alreadyTransmitted = (order?.transmitted || "").toLowerCase() === "yes";
  const photos = order?.photos || [];

  const togglePhoto = (id) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedPhotos.size === photos.length && photos.length > 0) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos.map(p => p.id)));
    }
  };
  const downloadZip = async (ids) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/aly/order/${orderId}/photos/zip`,
        { photo_ids: ids || [] },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${order?.order_number || orderId}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  // 2026-05-26 (Angela hard-lock fix) — per-row download.
  // Copied verbatim from NationalPhotosPanel.jsx `downloadOne()`. The previous
  // <a href download target="_blank"> only opened the image in a new tab on
  // iOS Safari and most desktop browsers because the file is served from a
  // different origin (REACT_APP_BACKEND_URL), which makes the `download`
  // attribute be ignored. Blob-fetch + programmatic anchor click is the
  // pattern that actually saves the file.
  const downloadOne = async (photo, index) => {
    if (!photo) return;
    setDownloading(true);
    try {
      const fileUrl = photo.file_url?.startsWith("http")
        ? photo.file_url
        : `${process.env.REACT_APP_BACKEND_URL}${photo.file_url || ""}`;
      const res = await axios.get(fileUrl, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.file_name || photo.original_name || `photo-${(index ?? 0) + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(`Failed to download ${photo.file_name}:`, e);
      // iOS Safari fallback: open in new tab → long-press → Save Image.
      try {
        const fb = photo.file_url?.startsWith("http")
          ? photo.file_url
          : `${process.env.REACT_APP_BACKEND_URL}${photo.file_url || ""}`;
        window.open(fb, "_blank");
      } catch (_) { /* ignore */ }
    } finally { setDownloading(false); }
  };
  const allSelected = photos.length > 0 && selectedPhotos.size === photos.length;

  const content = (
    <div className="max-w-[900px] mx-auto p-4 sm:p-6 space-y-5" data-testid="aly-order-detail">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(-1)} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="aly-order-back">
          <ArrowLeft className="h-4 w-4 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-[#1F2937]">Order {order?.order_number || orderId.slice(0, 8)}</h1>
            <span className="px-2 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
              <Lock className="h-3 w-3" /> View only
            </span>
          </div>
          {order && (
            <p className="text-sm text-gray-500 mt-0.5">
              {order.company} · {order.cliente || "—"} {order.flow_type ? `· ${order.flow_type}` : ""}
            </p>
          )}
        </div>
      </div>

      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">Loading order…</div>
      )}
      {err && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-700">{err}</div>
      )}

      {order && (
        <>
          {/* Status strip */}
          <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Status</span>
            <Chip tone="#2F5D8A" bg="#EAF1F9">{order.status || "—"}</Chip>
            <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider ml-2">Transmitted</span>
            {alreadyTransmitted
              ? <Chip tone="#4F7046" bg="#E3EEDF"><CheckCircle className="h-3 w-3 inline-block mr-1 -mt-0.5" />Yes · {order.transmitted_by || "—"}</Chip>
              : <Chip tone="#8A5A1F" bg="#FAE8C6">Not Transmitted</Chip>}
            <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider ml-2">Day Closed</span>
            {(order.daily_closed || "").toLowerCase() === "yes"
              ? <Chip tone="#4F7046" bg="#E3EEDF">Closed {order.daily_closed_date ? `· ${order.daily_closed_date}` : ""}</Chip>
              : <Chip tone="#6B7280" bg="#F3F4F6">Pending</Chip>}
          </div>

          {/* Order Actions — Transmission only */}
          <section className="bg-white border border-gray-200 rounded-xl p-4" data-testid="aly-order-actions">
            <h2 className="text-xs uppercase font-bold tracking-wider text-gray-500 mb-3">Order Actions</h2>
            <button
              type="button"
              onClick={() => { setTransmitErr(null); setShowTransmitModal(true); }}
              disabled={alreadyTransmitted}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                alreadyTransmitted
                  ? "bg-[#E3EEDF] text-[#4F7046] cursor-not-allowed"
                  : "bg-[#B08A3E] hover:bg-[#8F6E2D] text-white shadow-sm"
              }`}
              data-testid="aly-transmission-btn"
            >
              <Radio className="h-4 w-4" />
              {alreadyTransmitted
                ? `Transmitted by ${order.transmitted_by || "Aly"}${order.transmitted_at ? ` · ${new Date(order.transmitted_at).toLocaleString()}` : ""}`
                : "Transmission"}
            </button>
            <p className="mt-2 text-[10px] text-gray-400">
              Aly is the only role allowed to register National transmissions from this screen. This counts toward your pay period totals.
            </p>
          </section>

          {/* Inspector Photos — simple file list + bulk download actions */}
          <section className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="aly-order-photos">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 flex-wrap">
              <h2 className="text-xs uppercase font-bold tracking-wider text-gray-500 flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5 text-[#B08A3E]" />
                Inspector Uploads / Photos
                <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold">{photos.length}</span>
              </h2>
              {photos.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button type="button" onClick={toggleSelectAll}
                    className="flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                    data-testid="aly-photos-select-all">
                    {allSelected ? <CheckSquare className="h-3.5 w-3.5 text-[#B08A3E]" /> : <Square className="h-3.5 w-3.5 text-gray-400" />}
                    {allSelected ? "Unselect all" : "Select all"}
                  </button>
                  <button type="button" onClick={() => downloadZip(Array.from(selectedPhotos))}
                    disabled={downloading || selectedPhotos.size === 0}
                    className="flex items-center gap-1 px-2 py-1 bg-white border border-[#2F5D8A] text-[#2F5D8A] rounded text-[11px] font-semibold hover:bg-[#2F5D8A]/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="aly-photos-download-selected">
                    <Download className="h-3.5 w-3.5" />
                    Download selected ({selectedPhotos.size})
                  </button>
                  <button type="button" onClick={() => downloadZip([])}
                    disabled={downloading}
                    className="flex items-center gap-1 px-2 py-1 bg-[#B08A3E] text-white rounded text-[11px] font-semibold hover:bg-[#8F6E2D] disabled:opacity-60"
                    data-testid="aly-photos-download-all">
                    {downloading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    Download all
                  </button>
                </div>
              )}
            </div>
            {photos.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-8 text-center">No photos uploaded yet for this order.</p>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="aly-photos-list">
                {photos.map((p, idx) => {
                  const selected = selectedPhotos.has(p.id);
                  const displayName = p.file_name || `Photo ${idx + 1}`;
                  return (
                    <li key={p.id} className={`flex items-center gap-3 px-4 py-2 hover:bg-gray-50 ${selected ? "bg-[#FDF5E7]/40" : ""}`} data-testid={`aly-photo-row-${p.id}`}>
                      <button type="button" onClick={() => togglePhoto(p.id)}
                        className="flex-shrink-0"
                        aria-label={selected ? "Deselect" : "Select"}
                        data-testid={`aly-photo-check-${p.id}`}>
                        {selected
                          ? <CheckSquare className="h-4 w-4 text-[#B08A3E]" />
                          : <Square className="h-4 w-4 text-gray-300 hover:text-gray-500" />}
                      </button>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${p.file_url}`}
                        alt={displayName}
                        loading="lazy"
                        className="w-10 h-10 rounded border border-gray-200 object-cover flex-shrink-0 bg-gray-100"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 font-medium truncate" title={displayName}>{displayName}</p>
                        <p className="text-[10px] text-gray-400">
                          {p.uploaded_at ? new Date(p.uploaded_at).toLocaleString() : "—"}
                          {p.uploaded_by ? ` · ${p.uploaded_by}` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadOne(p, p.id)}
                        disabled={downloading}
                        className="p-1.5 text-gray-400 hover:text-[#2F5D8A] hover:bg-[#2F5D8A]/5 rounded disabled:opacity-50"
                        title="Download this file"
                        data-testid={`aly-photo-dl-${p.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Read-only order info */}
          <section className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="aly-order-info">
            <div className="px-4 py-3 border-b border-gray-100 bg-[#FDF5E7]/40">
              <h2 className="text-xs uppercase font-bold tracking-wider text-gray-600 flex items-center gap-1.5">
                <Flag className="h-3.5 w-3.5 text-[#B08A3E]" />
                Order Information
              </h2>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 p-4 text-sm">
              <Field label="Order Number" value={order.order_number} mono />
              <Field label="Client" value={order.cliente} />
              <Field label="Flow / Type" value={`${order.flow_type || "—"}${order.order_type ? ` · ${order.order_type}` : ""}`} />
              <Field label="PH Name" value={order.ph_name} />
              <Field label="Address" value={order.address} icon={MapPin} />
              <Field label="City / State / Zip" value={`${order.city || "—"}${order.state ? `, ${order.state}` : ""}${order.zip_code ? ` ${order.zip_code}` : ""}`} />
              <Field label="Due Date" value={order.due_date} icon={Calendar} />
              <Field label="Assigned Inspector" value={order.assigned_inspector || "Unassigned"} icon={User} />
            </dl>
          </section>

          {/* Appointment Contact */}
          <section className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="aly-order-appt">
            <div className="px-4 py-3 border-b border-gray-100 bg-[#EAF1F9]/40">
              <h2 className="text-xs uppercase font-bold tracking-wider text-gray-600 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-[#2F5D8A]" />
                Appointment
              </h2>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 p-4 text-sm">
              <Field label="Appointment Set" value={order.appointment_set === "yes" ? "Yes" : "No"} />
              <Field label="Window" value={order.appointment_window} icon={Clock} />
              <Field label="Scheduled Date" value={apptDate} />
              <Field label="Scheduled Time" value={apptTime} />
              <Field label="Who Will Be At Property" value={order.appointment_contact_name} icon={User} />
              <Field label="Contact Phone" value={order.appointment_contact_phone} icon={Phone} />
              <Field label="Agent" value={order.agt_name} />
              <Field label="Agent Phone" value={order.agt_phone} icon={Phone} />
            </dl>
          </section>

          {order.notes && (
            <section className="bg-white border border-gray-200 rounded-xl p-4" data-testid="aly-order-notes">
              <h2 className="text-xs uppercase font-bold tracking-wider text-gray-500 mb-2">Notes</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.notes}</p>
            </section>
          )}
        </>
      )}

      {/* Transmission confirmation modal */}
      {showTransmitModal && (
        <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4" data-testid="aly-transmit-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-[#1F2937] flex items-center gap-1.5">
                <Radio className="h-4 w-4 text-[#B08A3E]" /> Confirm Transmission
              </h3>
              <button onClick={() => setShowTransmitModal(false)} className="p-1 hover:bg-gray-100 rounded" data-testid="aly-transmit-close">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <Row k="Order">{order?.order_number}</Row>
              <Row k="Transmitted by">
                <span className="font-semibold text-[#B08A3E]">ALY</span>
                <span className="ml-1 text-[10px] uppercase font-bold text-[#B08A3E] bg-[#B08A3E]/10 px-1.5 py-0.5 rounded">default</span>
              </Row>
              <Row k="Date / Time">{nowLabel}</Row>
              <Row k="Payment">Inspector · counts toward your pay period</Row>
            </div>
            {transmitErr && <p className="text-xs text-red-600 mt-3">{transmitErr}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowTransmitModal(false)} disabled={transmitting} className="px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={confirmTransmit}
                disabled={transmitting}
                className="px-3 py-1.5 bg-[#B08A3E] hover:bg-[#8F6E2D] text-white text-xs font-semibold rounded disabled:opacity-60 flex items-center gap-1.5"
                data-testid="aly-transmit-confirm"
              >
                {transmitting ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                Confirm Transmission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No lightbox — Aly just needs file list + download. Keep minimal. */}
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
};

const Chip = ({ children, tone, bg }) => (
  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ color: tone, backgroundColor: bg }}>
    {children}
  </span>
);

const Field = ({ label, value, icon: Icon, mono }) => (
  <div>
    <dt className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">{label}</dt>
    <dd className={`text-sm text-gray-800 mt-0.5 flex items-center gap-1.5 ${mono ? "font-mono" : ""}`}>
      {Icon ? <Icon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : null}
      <span className="truncate">{value || "—"}</span>
    </dd>
  </div>
);

const Row = ({ k, children }) => (
  <div className="flex items-start justify-between py-1 border-b border-gray-50 last:border-0">
    <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{k}</span>
    <span className="text-sm text-gray-800 text-right ml-3">{children}</span>
  </div>
);

export default AlyOrderDetailPage;
